import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, DialogTrigger, Popover, Divider, Checkbox } from '@react-spectrum/s2';
import { ChevronUp, ChevronDown, DataSettings } from '../icons/s2';
// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------
export interface ColumnMeta {
    /** Stable key matching the column definition's `key`. */ key: string;
    /** Human-readable label shown in the configurator. */ label: string;
    /** Pinned columns are always visible and cannot be reordered. */ pinned?: boolean;
}
export interface ColumnConfiguratorProps {
    /** Full list of columns with display metadata. */ allColumns: ColumnMeta[];
    /** Current column order (array of keys). */ columnOrder: string[];
    /** Currently visible column keys. */ visibleKeys: ReadonlySet<string>;
    /** Called when the user reorders columns. */ onOrderChange: (order: string[]) => void;
    /** Called when the user toggles column visibility. */ onVisibilityChange: (visible: Set<string>) => void;
}
// ---------------------------------------------------------------------------
// Hook — encapsulates column order + visibility state
// ---------------------------------------------------------------------------
export interface UseColumnConfigOptions {
    columns: ColumnMeta[];
    /** Keys of columns visible by default. Omit to show all. */ defaultVisible?: string[];
}
export interface UseColumnConfigResult {
    columnOrder: string[];
    visibleKeys: Set<string>;
    setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
    setVisibleKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
}
export function useColumnConfig({ columns, defaultVisible }: UseColumnConfigOptions): UseColumnConfigResult {
    const defaultOrder = useMemo(()=>columns.map((c)=>c.key), [
        columns
    ]);
    const defaultSet = useMemo(()=>new Set(defaultVisible ?? defaultOrder), [
        defaultVisible,
        defaultOrder
    ]);
    const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(defaultSet);
    return {
        columnOrder,
        visibleKeys,
        setColumnOrder,
        setVisibleKeys
    };
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const ColumnConfigurator: React.FC<ColumnConfiguratorProps> = ({ allColumns, columnOrder, visibleKeys, onOrderChange, onVisibilityChange })=>{
    const { t } = useTranslation();
    const colMap = useMemo(()=>new Map(allColumns.map((c)=>[
                c.key,
                c
            ])), [
        allColumns
    ]);
    const orderedColumns = useMemo(()=>columnOrder.map((k)=>colMap.get(k)).filter((c): c is ColumnMeta =>c != null), [
        columnOrder,
        colMap
    ]);
    const pinnedCount = useMemo(()=>orderedColumns.filter((c)=>c.pinned).length, [
        orderedColumns
    ]);
    const toggleVisibility = useCallback((key: string)=>{
        const next = new Set(visibleKeys);
        if (next.has(key)) {
            if (next.size <= 1) return;
            next.delete(key);
        } else {
            next.add(key);
        }
        onVisibilityChange(next);
    }, [
        visibleKeys,
        onVisibilityChange
    ]);
    const moveUp = useCallback((key: string)=>{
        const idx = columnOrder.indexOf(key);
        if (idx <= pinnedCount) return;
        const next = [
            ...columnOrder
        ];
        [next[idx - 1], next[idx]] = [
            next[idx],
            next[idx - 1]
        ];
        onOrderChange(next);
    }, [
        columnOrder,
        pinnedCount,
        onOrderChange
    ]);
    const moveDown = useCallback((key: string)=>{
        const idx = columnOrder.indexOf(key);
        if (idx < 0 || idx >= columnOrder.length - 1) return;
        const next = [
            ...columnOrder
        ];
        [next[idx], next[idx + 1]] = [
            next[idx + 1],
            next[idx]
        ];
        onOrderChange(next);
    }, [
        columnOrder,
        onOrderChange
    ]);
    return (<DialogTrigger>
      <ActionButton isQuiet aria-label={t('components.columnConfigurator.showHideColumns')}>
        <DataSettings/>
      </ActionButton>
      <Popover placement="bottom end">
        <h3 className={" uk171 ucJ9TBTb171 ud3Euai171 uea171 ugb171 uhd171 uje171 u2NhKxcl171 uic171 -_6BNtrc-f171 vx171 xe171 _xa171 xX0cczbd171 _xX0cczba171 xfe171 _xfa171 xfX0cczbd171 _xfX0cczba171 _Fd171 _FnuYUweb171 po171 Jy171 Gv171"}>{t('components.columnConfigurator.columns')}</h3>
        <Divider/>
        <div className={" sd171 _ta171 Ub171 qb171 Jv171"}>
          {orderedColumns.map((col, idx)=>(<div key={col.key} className={" sd171 eb171 _Ce171 Ue171 qe171"} style={{
            padding: '4px 0'
        }}>
              <Checkbox isSelected={visibleKeys.has(col.key)} onChange={()=>toggleVisibility(col.key)} isDisabled={col.pinned}>
                {col.label}
              </Checkbox>
              {!col.pinned && (<div className={" sd171 Uh171 qh171"}>
                  <ActionButton isQuiet aria-label={t('components.columnConfigurator.moveUp', {
            label: col.label
        })} isDisabled={idx <= pinnedCount} onPress={()=>moveUp(col.key)}>
                    <ChevronUp size="S"/>
                  </ActionButton>
                  <ActionButton isQuiet aria-label={t('components.columnConfigurator.moveDown', {
            label: col.label
        })} isDisabled={idx >= orderedColumns.length - 1} onPress={()=>moveDown(col.key)}>
                    <ChevronDown size="S"/>
                  </ActionButton>
                </div>)}
            </div>))}
        </div>
      </Popover>
    </DialogTrigger>);
};

import "macro-6ec834d1f1e1efcafd72f6fcd2e8a77000cd2aca0d4bd42f708667639723e0bc.css";
import "macro-c8cf2648893df36de794443d622c69eefcf8019f16f7399d73ef9fb7b07b30c4.css";
import "macro-6e377b4078a09889196d12c6ae270d48bbe27cc783fc3e85f9387771b23312b9.css";
import "macro-cdd462178e273ddbb90eb4dd6c717bda802506b805c7a7445ab6a3b66b00efc3.css";